import { IconButton, Stack, Typography, useMediaQuery, Tooltip } from "@mui/material";
import { HashConnect } from "hashconnect";
import { MenuConfig } from "../configs/MenuConfig";
import { useState } from "react";
import brandLogo from "./../assets/brand.png";
import { useDispatch, useSelector } from "react-redux";
import { logout, setRedirectUrl, setShowLogin } from "../Redux/user/slice";
import LogoutIcon from "@mui/icons-material/Logout";
import {
  createStyles,
  Header,
  Group,
  Button,
  UnstyledButton,
  ThemeIcon,
  Box,
  Burger,
  rem,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { Link, useLocation, useNavigate } from "react-router-dom";
import React, { useEffect } from "react";
import { FormattedMessage } from "react-intl";
import { setLanguage } from "../Redux/languageSlice";

const useStyles = createStyles((theme) => ({
  link: {
    display: "flex",
    alignItems: "center",
    height: "60%",
    paddingLeft: theme.spacing.md,
    paddingRight: theme.spacing.md,
    textDecoration: "none",
    color: theme.colorScheme === "dark" ? theme.white : theme.black,
    fontWeight: 500,
    fontSize: theme.fontSizes.md,
    margin: "0 5px", // Space between buttons

    [theme.fn.smallerThan("xs")]: {
      height: rem(42),
      display: "flex",
      alignItems: "center",
      width: "100%",
      margin: "0", // Avoid extra space in mobile view
    },

    ...theme.fn.hover({
      backgroundColor: theme.colorScheme === "dark" ? theme.colors.dark[6] : theme.colors.gray[0],
    }),

    "&:active": theme.activeStyles,
  },

  subLink: {
    width: "100%",
    padding: `${theme.spacing.xs} ${theme.spacing.md}`,
    borderRadius: theme.radius.md,

    ...theme.fn.hover({
      backgroundColor: theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.colors.gray[0],
    }),

    "&:active": theme.activeStyles,
  },

  hiddenMobile: {
    [theme.fn.smallerThan("sm")]: {
      display: "none",
    },
  },

  hiddenDesktop: {
    [theme.fn.largerThan("sm")]: {
      display: "none",
    },
  },
}));

const MenuPanel = ({ level }) => {
  const dispatch = useDispatch();
  const { redirectUrl, isUserLoggedIn, showLogin } = useSelector((state) => state.user);
  const selectedLanguage = useSelector((state) => state.language);
  const navigate = useNavigate(); // Add useNavigate hook

  const [openQr, setOpenQR] = useState(false);
  const [pairingString, setPairingString] = useState(false);
  const { userInfo } = useSelector((state) => state.user);
  const userinfo = JSON.parse(localStorage.getItem("userinfo"));
  // Use Redux userInfo or fallback to localStorage
  const displayUserInfo = userInfo?.userId ? userInfo : userinfo;
  const [drawerOpened, { toggle: toggleDrawer, close: closeDrawer }] = useDisclosure(false);
  const [linksOpened, { toggle: toggleLinks }] = useDisclosure(false);
  const { classes, theme } = useStyles();

  const handleSignUpClick = () => {
    navigate('/signup-form');
  };

  const handleLogout = () => {
    // Retrieve and set the selected language from local storage
    dispatch(logout());

    localStorage.removeItem("userLogged");
    localStorage.removeItem("userinfo");
    // Preserve tour completion on logout

    dispatch(setLanguage(selectedLanguage));

    if (window.location.pathname === "/my-list") {
      navigate('/'); // Use navigate instead of window.location
    }
  };

  const handleLoginClick = () => {
    dispatch(setShowLogin({ value: true, cb: null }));
  };

  const handleLanguageChange = (language) => {
    const oldLanguage = JSON.parse(localStorage.getItem("language"));
    dispatch(setLanguage(language)); // Dispatch the setLanguage action to update the selected language
    localStorage.setItem("language", JSON.stringify(language));
  };

  const languageOptions = [
    { value: "en", label: <FormattedMessage id="languageOptions.en" /> },
    { value: "fr", label: <FormattedMessage id="languageOptions.fr" /> },
    { value: "es", label: <FormattedMessage id="languageOptions.es" /> },
    { value: "pt", label: <FormattedMessage id="languageOptions.pt" /> },
  ];

  // Handle redirect after login
  useEffect(() => {
    if (redirectUrl && isUserLoggedIn && !showLogin) {
      navigate(redirectUrl);
      dispatch(setRedirectUrl({ redirectUrl: "" }));
    }
  }, [isUserLoggedIn, redirectUrl, showLogin, navigate, dispatch]);

  return (
    <>
      <Box pb={10} style={{ position: "relative", zIndex: 100 }}>
        <Header height={70} style={{ zIndex: 100, position: "relative" }}>
          <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
            <Link to="/" style={{ display: "flex", alignItems: "center", height: "70px", flexShrink: 0 }}>
              <img src={brandLogo} alt="Brand Logo" style={{ 
                  maxHeight: "62px", 
                  maxWidth: "100%", 
                  objectFit: "contain" 
                }} />
            </Link>

            <Group spacing={0} className={classes.hiddenMobile}>
              {MenuConfig.filter((x) => level === "3" || x.name !== "User").map((item, index) => (
                <MenuItem key={index} Value={item} className={classes.link} selectedLanguage={selectedLanguage} />
              ))}
            </Group>
            <Group position="right" style={{ marginLeft: "auto" }}>
              {displayUserInfo?.userId ? (
                <>
                  <Typography
                    className={classes.link}
                    sx={{
                      fontSize: "smaller",
                      backgroundColor: "#FFA500",
                      color: "white",
                      padding: "8px",
                      borderRadius: "4px",
                    }}
                  >
                    <FormattedMessage id="userInfo.acctID" /> {displayUserInfo.accountId}
                    <br />
                    <FormattedMessage id="userInfo.userID" /> {displayUserInfo.userId}
                  </Typography>
                  <Button onClick={handleLogout} sx={{ color: "black" }}>
                    <LogoutIcon /> <FormattedMessage id="button.logout" />
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="default" onClick={handleLoginClick} style={{ margin: "0 5px" }}>
                    <FormattedMessage id="button.login" />
                  </Button>

                  <Button onClick={handleSignUpClick} style={{ margin: "0 5px" }}>
                    <FormattedMessage id="button.signup" />
                  </Button>
                </>
              )}
              <select
                value={selectedLanguage}
                onChange={(e) => handleLanguageChange(e.target.value)}
                style={{ marginRight: "25px", fontSize: "12px" }} // Adjust the margin to create space between the dropdown and the links
              >
                {languageOptions.map((language) => (
                  <option key={language.value} value={language.value}>
                    {language.label}
                  </option>
                ))}
              </select>
            </Group>
          </div>
        </Header>
      </Box>
    </>
  );
};

const MenuItem = ({ Value, className, selectedLanguage }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isUserLogged = localStorage.getItem("userLogged");
  
  const activate = (e) => {
    console.log("MenuItem clicked:", Value.name, "URL:", Value.url, "Auth required:", Value.enableAuthentication, "User logged:", isUserLogged);
    e.preventDefault();
    const { url, enableAuthentication, newPage } = Value;

    // Check if authentication is required and user is not logged in
    if (enableAuthentication && !isUserLogged) {
      console.log("Showing login modal for:", url);
      dispatch(setRedirectUrl({ redirectUrl: url }));
      dispatch(setShowLogin({ value: true, cb: null }));
    } else if (newPage) {
      console.log("Opening in new page:", url);
      window.open(url, "_blank");
    } else {
      console.log("Navigating to:", url);
      navigate(url);
    }
  };

  // Check if the current location matches the item's URL
  const isActive = location.pathname === Value.url;

  return (
    <a
      href={Value.url}
      className={className}
      onClick={activate}
      style={{
        textDecoration: "none",
        color: isActive ? "#fff" : "#000",
        fontWeight: 500,
        fontSize: "14px",
        padding: "8px 8px",
        backgroundColor: isActive ? "#009688" : "transparent",
        borderRadius: "4px",
        cursor: "pointer",
      }}
    >
      <FormattedMessage id={`menu.${Value.name}`} />
    </a>
  );
};

export default MenuPanel;